import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {PriceIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {MaxValueFieldControlled} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {priceFormat} from "@pg-mono/string-utils";

import {IApplication} from "../../../../application/types/IApplication";
import {IApplicationFormValues} from "../../../../application/types/IApplicationFormValues";
import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {getCurrency} from "../../../../utils/get_currency";
import {isValidationError} from "../../../../utils/isValidationError";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {FormSubmitFooter} from "../../FormSubmitFooter";
import {UserContactDataForm} from "../../UserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    })
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    negotiation: number;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
    price: number | null;
}

export const NegotiationForm = (props: IProps) => {
    const {handleModalClose, onSuccessfulSubmit, storeHash, initialLeadFormValues, offerId, customHeader, price} = props;

    if (!price) return <Loader />;

    const [isSending, setIsSending] = useState(false);

    const {email, marketing_email, marketing_sms, name, phone} = initialLeadFormValues || {};

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const currency = useAppSelector((state) => state.mainLead.currency);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);

    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: name || "",
        email: email || "",
        phone: phone || {code: 48, number: ""},
        marketing_email: marketing_email || false,
        marketing_sms: marketing_sms || false,
        negotiation: price
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit} = formMethods;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, leadModalState.sourceSection);

            const {name, email, phone} = initialLeadFormValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues = {...defaultValues, ...values};

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose)) as unknown as IApplication;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    {customHeader}

                    <UserContactDataForm />

                    <Text variant="body_copy_2" mb={3} className={css({display: "flex", alignItems: "center", gap: 1})}>
                        <PriceIcon className={css({width: 24, height: 24})} /> Cena ofertowa:{" "}
                        <Text as="span" variant="headline_6">
                            <Highlight>{priceFormat(price, {unit: getCurrency({currency})})}</Highlight>
                        </Text>
                    </Text>

                    <MaxValueFieldControlled
                        max={price}
                        min={Math.ceil(price * 0.9)}
                        step={1000}
                        name="negotiation"
                        defaultValue={price}
                        useTriColorSlider
                        rightElement={getCurrency({currency})}
                        className={css({mb: 3})}
                        labelContent="Twoja propozycja ceny"
                    />

                    <FormSubmitFooter isSending={isSending} buttonText="Negocjuj cenę" />
                </form>
            </FormProvider>
        </Suspense>
    );
};
import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {PriceIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {MaxValueFieldControlled} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {priceFormat} from "@pg-mono/string-utils";

import {IApplication} from "../../../../application/types/IApplication";
import {IApplicationFormValues} from "../../../../application/types/IApplicationFormValues";
import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {getCurrency} from "../../../../utils/get_currency";
import {isValidationError} from "../../../../utils/isValidationError";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {FormSubmitFooter} from "../../FormSubmitFooter";
import {UserContactDataForm} from "../../UserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    })
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    negotiation: number;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
    price: number | null;
}

export const NegotiationForm = (props: IProps) => {
    const {handleModalClose, onSuccessfulSubmit, storeHash, initialLeadFormValues, offerId, customHeader, price} = props;

    if (!price) return <Loader />;

    const [isSending, setIsSending] = useState(false);

    const {email, marketing_email, marketing_sms, name, phone} = initialLeadFormValues || {};

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const currency = useAppSelector((state) => state.mainLead.currency);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);

    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: name || "",
        email: email || "",
        phone: phone || {code: 48, number: ""},
        marketing_email: marketing_email || false,
        marketing_sms: marketing_sms || false,
        negotiation: price
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit} = formMethods;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, leadModalState.sourceSection);

            const {name, email, phone} = initialLeadFormValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues = {...defaultValues, ...values};

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose)) as unknown as IApplication;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    {customHeader}

                    <UserContactDataForm />

                    <Text variant="body_copy_2" mb={3} className={css({display: "flex", alignItems: "center", gap: 1})}>
                        <PriceIcon className={css({width: 24, height: 24})} /> Cena ofertowa:{" "}
                        <Text as="span" variant="headline_6">
                            <Highlight>{priceFormat(price, {unit: getCurrency({currency})})}</Highlight>
                        </Text>
                    </Text>

                    <MaxValueFieldControlled
                        max={price}
                        min={Math.ceil(price * 0.9)}
                        step={1000}
                        name="negotiation"
                        defaultValue={price}
                        useTriColorSlider
                        rightElement={getCurrency({currency})}
                        className={css({mb: 3})}
                        labelContent="Twoja propozycja ceny"
                    />

                    <FormSubmitFooter isSending={isSending} buttonText="Negocjuj cenę" />
                </form>
            </FormProvider>
        </Suspense>
    );
};
